/**
 * The HTTP transport. Every request the app makes goes through here.
 *
 * Written in JS with JSDoc rather than TypeScript — this repo has no tsconfig
 * and no TS toolchain, so the interfaces from the reference implementation are
 * expressed as typedefs. Editors still get the shapes; nothing needs compiling.
 *
 * There is no Environment module here either: the dashboard is served from the
 * same origin as its API, so the base is a path rather than a host. `configure`
 * exists so that can change without touching a service.
 *
 * ── Why the token is not a parameter ─────────────────────────────────────────
 * A 401 on ANY request has to sign the user out, and the token lives in React
 * state. Rather than thread both through every service call, AuthProvider calls
 * Api.configure() with a getter and a callback. That keeps service functions to
 * a single payload argument while leaving one place where auth is handled.
 *
 * @typedef {Object} ApiOptions
 * @property {any}            [body]     Serialised as JSON when present.
 * @property {AbortSignal}    [signal]   Cancels the request.
 * @property {Object}         [headers]  Merged over the defaults.
 * @property {boolean}        [auth]     Send the bearer token. Default true.
 *
 * @template T
 * @typedef {Object} ApiResponse
 * @property {T}      response  The parsed body.
 * @property {number} status    The HTTP status.
 */

/** Identifies this client in the server logs. Inert server-side today. */
export const API_CHANNEL = 'affiliate-dashboard';

const DEFAULTS = {
  base: '/api',
  getToken: () => null,
  onUnauthorized: () => {},
};

let config = { ...DEFAULTS };

/*
 * Navigating away rejects every in-flight fetch with a bare
 * `TypeError: Failed to fetch` — NOT an AbortError, because it is the browser
 * tearing the page down rather than us cancelling. Logging those makes the
 * console cry wolf on every reload, so track the teardown and stay quiet.
 */
let unloading = false;
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => { unloading = true; });
}

/** An abort or a page teardown is not a failure and must not be logged. */
const isQuiet = (error, signal) =>
  error?.name === 'AbortError' || signal?.aborted || unloading;

export class Api {
  /**
   * Install the auth hooks and, if it ever differs, the base path.
   * Called once by AuthProvider.
   */
  static configure(next) {
    config = { ...config, ...next };
  }

  /** Restores the defaults. Tests use this; the app does not. */
  static reset() {
    config = { ...DEFAULTS };
  }

  static getUrl() {
    return config.base;
  }

  /** The headers every request carries, before the caller's own are merged in. */
  static headers(options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      'x-api-channel': API_CHANNEL,
      ...options.headers,
    };

    // `auth: false` is for the one endpoint that runs before there is a token —
    // sending a stale one to /login would 401 and sign out the person trying to
    // sign in.
    if (options.auth !== false) {
      const token = config.getToken();
      if (token) headers.Authorization = `Bearer ${token}`;
    }
    return headers;
  }

  static init(method, options = {}) {
    const init = { method, headers: Api.headers(options), signal: options.signal };
    if (options.body !== undefined) init.body = JSON.stringify(options.body);
    return init;
  }

  /* ── The forgiving form ──────────────────────────────────────────────────
   * Resolves to the parsed body, or null on 401, network failure, abort or an
   * unparseable body. Callers already guard with `res?.field`, so a throw here
   * would break them silently.
   */

  static get(url, options = {}) { return Api.request(url, 'GET', options); }
  static post(url, options = {}) { return Api.request(url, 'POST', options); }
  static put(url, options = {}) { return Api.request(url, 'PUT', options); }
  static patch(url, options = {}) { return Api.request(url, 'PATCH', options); }
  static delete(url, options = {}) { return Api.request(url, 'DELETE', options); }

  static async request(url, method, options = {}) {
    const target = Api.getUrl() + url;

    let res;
    try {
      res = await fetch(target, Api.init(method, options));
    } catch (e) {
      if (isQuiet(e, options.signal)) return null;
      console.error('[api] network error', method, target, e);
      return null;
    }

    if (res.status === 401) {
      console.warn('[api] 401 on', method, target, '— logging out');
      config.onUnauthorized();
      return null;
    }
    if (!res.ok) console.error('[api]', res.status, method, target);

    // The abort can land here too, not just on the fetch above: aborting while
    // the body is still being read rejects res.json() with an AbortError.
    return res.json().catch(e => {
      if (isQuiet(e, options.signal)) return null;
      console.error('[api] JSON parse error:', e);
      return null;
    });
  }

  /* ── The explicit form ───────────────────────────────────────────────────
   * Resolves to { response, status }. Use it where the status itself is the
   * answer — a 409 conflict, a 202 accepted — rather than something to hide.
   * It does NOT swallow failures: a network error rejects.
   */

  static getV2(url, options = {}) { return Api.requestV2(url, 'GET', options); }
  static postV2(url, options = {}) { return Api.requestV2(url, 'POST', options); }
  static putV2(url, options = {}) { return Api.requestV2(url, 'PUT', options); }
  static patchV2(url, options = {}) { return Api.requestV2(url, 'PATCH', options); }
  static deleteV2(url, options = {}) { return Api.requestV2(url, 'DELETE', options); }

  static async requestV2(url, method, options = {}) {
    const target = Api.getUrl() + url;
    const res = await fetch(target, Api.init(method, options));

    if (res.status === 401) config.onUnauthorized();

    return {
      response: await res.json().catch(() => null),
      status: res.status,
    };
  }
}

/**
 * Build a query string, dropping anything empty.
 *
 * Services use this so a blank filter never reaches the server as `min=` —
 * which the archive endpoint reads as zero rather than "unset".
 */
export function query(params) {
  const search = new URLSearchParams();
  for (const [k, v] of Object.entries(params ?? {})) {
    if (v === '' || v == null) continue;
    search.set(k, String(v));
  }
  const qs = search.toString();
  return qs ? `?${qs}` : '';
}

export default Api;
